/* eslint-disable import/order */
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Switch as SwitchMaterial,
  Tooltip,
} from '@mui/material';
import { AlertColor as AlertSeverity } from '@mui/material/Alert';
import { GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { AxiosResponse, Method } from 'axios';
import { ValidationError } from 'class-validator';
import clsx from 'clsx';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import moment from 'moment';
import { OptionsObject, SnackbarKey } from 'notistack';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, UseMutationResult } from 'react-query';
import {
  generatePath,
  Link,
  NavLink,
  Route,
  Switch,
  useHistory,
} from 'react-router-dom';
import {
  SetterOrUpdater,
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import { Logger } from '../../common/Logger';
import { trackAddPersonalPowerList } from '../../common/userTracking';
import excelSVG from '../../images/excel.svg';
import { IUserAtom, Role, user } from '../../recoil/atoms/user';
import { refetchToggle } from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { toAbsoluteUrl } from '../../_metronic/_helpers';
import { DeletePowerlistModal } from '../components/modals/DeletePowerlistModal';
import {
  getMappingCRMId,
  nullIfHasMappingCrmId,
  PowerlistModal,
} from '../components/modals/PowerlistModal';
import { SpreadsheetsModal } from '../components/modals/SpreadsheetsModal';
import {
  IAlertWithProgressBar,
  useSnackbarWithStyles,
} from '../components/notifications/SnackbarProviderWithStyles';
import { priorityRange } from '../components/priorities';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { DashboardTourExtraSpotlights } from './dashboardTour/DashboardTourExtraSpotlights';
import { usePowerlistDataTableStyles } from './styles/usePowerlistDataTableStyles';

class Powerlist {
  id!: number;
  powerlistId!: number;
  name!: string;
  status!: 0 | 1;
  autostartOnLogon!: 0 | 1;
  mustDespositionCalls!: 0 | 1;
  ignoreDNC!: 0 | 1;
  assignToAgentOnAnswer!: 0 | 1;
  overrideOwnerOnAnswer!: 0 | 1;
  onlyDialFromOwnerIfOwned!: 0 | 1;
  secondsBeforeTimeout!: number;
  maxAttempts!: number;
  dialAtATime!: number;
  maxAttemptsPerDay!: number;
  minDurationBeforeCallRetryInMin!: number;
  maxWaitAfterCallInSec!: number;
  powerlistHours!: string;
  useBusinessHoursForPowerlist!: 0 | 1;
  targetHours!: string;
  useBusinessHoursForTarget!: 0 | 1;
  maxSmsPerTemplateCount!: number;
  maxSmsPerTemplateDurationInMin!: number;
  createdByUserId!: number;
  updatedDate!: string;
  createdDate!: string;
  type?: 'Organizational' | 'Personal';
}

// TODO: move to centeralized location
export enum ErrorEnum {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  POWERLIST_INVALID = 'POWERLIST_INVALID',
  POWERLIST_ACTION_REQUIRED = 'POWERLIST_ACTION_REQUIRED',
  POWERLIST_IS_FORWARDED_TO = 'POWERLIST_IS_FORWARDED_TO',
  POWERLIST_NOT_FOUND = 'POWERLIST_NOT_FOUND',
  FK_POWERLIST_ACTION__SMS_TEMPLATES = 'FK_POWERLIST_ACTION__SMS_TEMPLATES',
  FK_POWERLIST__QUEUES = 'FK_POWERLIST__QUEUES',
  FK_POWERLIST__BUSINESS_DISPOSITIONS = 'FK_POWERLIST__BUSINESS_DISPOSITIONS',
}

/**
 * Custom error containing ValidationError[]
 *
 * @see https://javascript.info/custom-errors
 */
export class ErrorOnValidate<T = any> extends Error {
  errorType?: ErrorEnum;
  validationErrors?: ValidationError[];
  entities?: T[];
  isErrorOnValidate: boolean;

  constructor(
    message: string,
    errorType?: ErrorEnum,
    errorData?: {
      validationErrors?: ValidationError[];
      entities?: T[];
    }
  ) {
    super(message); // (1)
    this.name = 'ErrorOnValidate'; // (2)
    this.isErrorOnValidate = true;
    this.errorType = errorType;
    this.validationErrors = errorData?.validationErrors;
    this.entities = errorData?.entities;
  }
}

const PowerlistToggle = (props: {
  params: GridRenderCellParams;
  disabled: boolean;
  mutation: UseMutationResult<
    AxiosResponse<any>,
    unknown,
    {
      endpoint: string;
      payload?:
        | {
            [key: string]: any;
          }
        | undefined;
      method?: Method | undefined;
      omitBusinessIdAndApikey?: boolean | undefined;
    },
    unknown
  >;
  enqueueSnackbarWithStyles: (
    alertComponentProps: IAlertWithProgressBar,
    snackbarProps?: OptionsObject | undefined
  ) => SnackbarKey | null;
  setRefetchToggleForPowerlists: SetterOrUpdater<any>;
  userAtom: IUserAtom;
}) => {
  const [checked, setChecked] = useState<boolean>(
    Boolean(props.params.value === 'active')
  );
  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ): Promise<void> => {
    setChecked(checked);
    try {
      await props.mutation.mutateAsync({
        endpoint: `${endpointEnum.TOGGLE_POWERLIST_STATUS}`,
        payload: {
          businessId: props.userAtom?.business?.businessid as any,
          powerlistId: props.params.row.id,
          userid: props.userAtom?.userid,
          apiKey: props.userAtom?.apikey,
          call: 'togglePowerlistStatus',
        } as Partial<Powerlist>,
        method: 'post',
      });

      // refetch business queues
      props.setRefetchToggleForPowerlists({});
    } catch (exception) {
      setChecked(!checked);
      const message: string | React.ReactNode =
        'Failed to change status of powerlist';
      const severity: AlertSeverity = 'error';
      props.enqueueSnackbarWithStyles({
        message,
        alertProps: {
          severity,
        },
      });
    }
  };

  return (
    <SwitchMaterial
      checked={checked}
      onChange={handleChange}
      color="primary"
      name="status"
      disabled={props.disabled}
      inputProps={{ 'aria-label': 'primary checkbox' }}
    />
  );
};

export function AgentPowerlists(props: { agentId: string; profile: boolean }) {
  const { agentId, profile } = props;
  const _classes = usePowerlistDataTableStyles();
  const history = useHistory();
  const [deleteItemId, setDeleteItemId] = useState<
    string | number | undefined
  >();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);
  const userAtom = userAtomLoadable.contents as IUserAtom;

  const teamPowerlistsLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      method: 'get',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: `${endpointEnum.GET_USER_TEAM_POWERLISTS}/${agentId}`,
    })
  );

  const { treatment } = useFeatureFlag('SWARM-31-org-pl-privacy');
  const isOrgPlPrivacyEnabled = useMemo(() => {
    return treatment === 'control' || treatment === 'on';
  }, [treatment]);

  const teamPowerlistsCached = useRef<Powerlist[]>([] as any);
  teamPowerlistsCached.current = useMemo(
    () =>
      teamPowerlistsLoadable.state === 'hasValue'
        ? teamPowerlistsLoadable?.contents?.response?.data.map((d) => ({
            id: d.powerlistId,
            type: d.isAgentPowerlist === 0 ? 'Organizational' : 'Personal',
            ...d,
          }))
        : teamPowerlistsCached.current,
    [teamPowerlistsLoadable]
  );

  const mutation = useMutation(getApiMutation(userAtom, setUserAtom));

  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents as IPermissions;
  const isSuperAdminBoolean = role === Role.SUPERADMIN;
  const loginUser = userAtom.user ?? {};

  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        dataType="Powerlists"
        // numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            dataPup="add-new-agent-powerlist"
            addButtonText="New Personal PowerList"
            // numSelected={selected.length}
            onClickForAdd={() => {
              history.push(
                profile
                  ? `/my-profile/${loginUser.userid}/powerlists/add`
                  : `/manage/agents/${agentId}/powerlists/add`
              );
              if (
                window.location.pathname ===
                `/my-profile/${loginUser.userid}/vm-drop`
              ) {
                trackAddPersonalPowerList();
              }
            }}
            isLoading={props.isLoading}
          />
        }
      />
    ),
    []
  );
  const setRefetchToggleForPowerlists = useSetRecoilState(
    refetchToggle(`${endpointEnum.GET_USER_TEAM_POWERLISTS}/${agentId}`)
  );
  const handlePreflightCheck = async (id: string | number) => {
    let message: string | React.ReactNode = 'Update sucessful'; // ! to ignore usage before assignment error
    let severity: AlertSeverity = 'success';
    try {
      const response = await mutation.mutateAsync({
        // placeholder for actual call
        endpoint: `${endpointEnum.DELETE_POWERLIST}`,
        method: 'options',
      });
      if (response.status === 200) {
        history.push(
          generatePath(
            profile
              ? `/my-profile/${loginUser.userid}/powerlists/:id/delete`
              : `/manage/agents/${agentId}/powerlists/:id/delete`,
            { id }
          )
        );
      }
    } catch (exception) {
      message = 'Failed to delete powerlist (please try again later)';
      severity = 'error';
      enqueueSnackbarWithStyles({
        message,
        alertProps: {
          severity,
        },
      });
    }
  };

  const handleDeletion = async (id: string | number) => {
    let message: string | React.ReactNode = 'Update sucessful'; // ! to ignore usage before assignment error
    let severity: AlertSeverity = 'success';

    try {
      const response = await mutation.mutateAsync({
        // placeholder for actual call
        endpoint: `${endpointEnum.DELETE_POWERLIST}`,
        method: 'POST',
        omitBusinessIdAndApikey: true,
        payload: {
          businessId: userAtom?.business?.businessid as any,
          powerlistId: Number(id),
          userid: userAtom?.userid,
          apiKey: userAtom?.apikey,
          call: 'deletePowerlist',
        },
      });

      // log success
      Logger.trace(
        {
          message,
          response,
        },
        'deletePowerlist'
      );

      // refetch business queues
      setRefetchToggleForPowerlists({});
    } catch (exception) {
      message = 'Failed to delete powerlist (please try again later)';
      severity = 'error';
      // log success
      Logger.trace(
        {
          message,
          exception,
        },
        'deletePowerlist'
      );
    }

    // clear selections list
    setDeleteItemId(undefined);

    enqueueSnackbarWithStyles({
      message,
      alertProps: {
        severity,
      },
    });
  };

  const setSelectedPowerlistId = (powerlistId) => {
    setUserAtom({
      ...userAtom,
      selectedPowerlistId: powerlistId,
    });
  };

  const getAgentPowerlistLink = ({
    params,
    profile,
    agentId,
    powerlistId,
    subpath,
  }: {
    params: GridRenderCellParams;
    profile: any;
    agentId: string;
    subpath: string;
    powerlistId?: string;
  }): string => {
    let editLink = profile
      ? `/my-profile/${agentId}/powerlists/${powerlistId ?? ':id'}/${subpath}`
      : `/manage/agents/${agentId}/powerlists/${
          powerlistId ?? ':id'
        }/${subpath}`;
    const mappingCrmId = getMappingCRMId(params.row);
    if (mappingCrmId) {
      editLink += `?mappingCrmId=${mappingCrmId}`;
    }
    return editLink;
  };

  const handlePowerListClone = async (powerlistId: number) => {
    let message: string | React.ReactNode = 'PowerList Cloned';
    let severity: AlertSeverity = 'success';
    try {
      const payload = {
        powerlistId: powerlistId,
        businessid: userAtom?.businessid,
        businessId: userAtom?.businessid,
        userId: userAtom?.userid,
        userid: userAtom?.userid,
      };

      const response = await mutation.mutateAsync({
        domain: process.env.VITE_APP_CDK_DOMAIN,
        stage: 'auto',
        endpoint: endpointEnum.CLONE_POWERLIST,
        method: 'post',
        payload,
      });
      message += `: ${response.data.data.name} created`;
      // log success
      Logger.trace({ message, response }, 'clonePowerlist');
    } catch (exception) {
      message = 'Failed to clone powerlist (please try again later)';
      severity = 'error';

      // log error
      Logger.trace({ message, exception }, 'clonePowerlist');
    }
    enqueueSnackbarWithStyles({
      message,
      alertProps: {
        severity,
      },
    });
    setRefetchToggleForPowerlists({});
  };

  const actionColumnFormatter = useCallback(
    (params: GridRenderCellParams) => {
      const hasMappingCrmId = !!getMappingCRMId(params.row);
      const powerlistType = params?.row?.type;
      const userRole = userAtom?.user?.role;

      let displayUserActions = true;
      if (powerlistType === 'Organizational' && userRole === 'user') {
        displayUserActions = false;
      }

      const viewContactsLink = getAgentPowerlistLink({
        params,
        profile,
        agentId,
        subpath: 'view-contacts',
      });

      const uploadStatusLink = getAgentPowerlistLink({
        params,
        profile,
        agentId,
        subpath: 'spreadsheets',
      });

      const editLink = getAgentPowerlistLink({
        params,
        profile,
        agentId,
        subpath: 'edit',
      });

      if (!displayUserActions) {
        return null;
      }

      return (
        <PopupState variant="popover">
          {(popupState) => (
            <>
              <Tooltip title="View actions">
                <IconButton {...bindTrigger(popupState)} size="large">
                  <KeyboardArrowDownIcon />
                </IconButton>
              </Tooltip>
              <Menu {...bindMenu(popupState)} disableScrollLock={true}>
                {(displayUserActions || !isOrgPlPrivacyEnabled) && (
                  <MenuItem
                    aria-label="view contacts"
                    onClick={() => {
                      setSelectedPowerlistId(params.row.powerlistId);
                      popupState.close();
                    }}
                    disabled={hasMappingCrmId}
                  >
                    <NavLink
                      to={generatePath(viewContactsLink, params.row)}
                      className={_classes.dropdownButton}
                      data-powerlistid={params.row.powerlistid}
                    >
                      <GroupIcon className={_classes.flushIconButton} />
                      <small style={{ marginLeft: 7 }}>View Contacts</small>
                    </NavLink>
                  </MenuItem>
                )}
                {displayUserActions && (
                  <>
                    <MenuItem
                      aria-label="upload status"
                      onClick={() => {
                        setSelectedPowerlistId(params.row.powerlistId);
                        popupState.close();
                      }}
                      disabled={hasMappingCrmId}
                    >
                      <NavLink
                        to={generatePath(uploadStatusLink, params.row)}
                        className={_classes.dropdownButton}
                        data-powerlistid={params.row.powerlistid}
                      >
                        <img
                          alt="upload status"
                          style={{ height: 20 }}
                          src={excelSVG}
                        />
                        <small style={{ marginLeft: 7 }}>Upload Status</small>
                      </NavLink>
                    </MenuItem>
                    <MenuItem
                      aria-label="clone powerlist"
                      onClick={async (e) => {
                        e.preventDefault();
                        await handlePowerListClone(params.row.powerlistId);
                        popupState.close();
                      }}
                    >
                      <div
                        className={_classes.dropdownButton}
                        data-powerlistid={params.row.powerlistid}
                      >
                        <ContentCopyIcon />
                        <small style={{ marginLeft: 7 }}>Clone PowerList</small>
                      </div>
                    </MenuItem>
                    <MenuItem
                      aria-label="edit powerlist"
                      onClick={(e) => {
                        e.preventDefault();
                        popupState.close();
                      }}
                    >
                      <NavLink
                        to={generatePath(editLink, params.row)}
                        className={_classes.dropdownButton}
                        data-powerlistid={params.row.powerlistid}
                      >
                        <CreateIcon className={_classes.editIconButton} />
                        <small style={{ marginLeft: 7 }}>Edit PowerList</small>
                      </NavLink>
                    </MenuItem>

                    <MenuItem
                      aria-label="delete"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePreflightCheck(params.row.id);
                        // popupState.close();
                      }}
                      disabled={hasMappingCrmId}
                    >
                      <div
                        className={_classes.dropdownButton}
                        data-powerlistid={params.row.powerlistid}
                      >
                        <DeleteIcon
                          className={clsx({
                            // enable styling only when not disabled
                            [_classes.deleteIconButton]: !hasMappingCrmId,
                          })}
                        />
                        <small style={{ marginLeft: 7 }}>
                          Delete PowerList
                        </small>
                      </div>
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          )}
        </PopupState>
      );
    },
    [
      deleteItemId,
      setDeleteItemId,
      mutation.isLoading,
      handlePreflightCheck,
      setSelectedPowerlistId,
      profile,
      props.agentId,
      _classes,
    ]
  );

  const columns = [
    {
      field: 'powerlistId',
      headerName: 'ID',
      headerClassName: 'superStyle',
      hide: !isSuperAdminBoolean,
      width: 90,
    },
    {
      field: 'status',
      headerName: 'Active',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <PowerlistToggle
          params={params}
          mutation={mutation}
          enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
          setRefetchToggleForPowerlists={setRefetchToggleForPowerlists}
          userAtom={userAtom}
          disabled={params.row.type === 'Organizational'}
        />
      ),
      disableExport: true,
    },
    {
      field: 'name',
      headerName: 'PowerList',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params: GridRenderCellParams) => {
        const powerlistId = params.row.powerlistId;

        const editLink = getAgentPowerlistLink({
          params,
          profile,
          agentId,
          powerlistId,
          subpath: 'edit',
        });

        return params.row.type === 'Personal' ? (
          <Tooltip title="Edit">
            <Link to={editLink} data-pup="edit-agent-powerlist-link">
              {params.value}
            </Link>
          </Tooltip>
        ) : (
          <div>{params.value}</div>
        );
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'powerlistPriority',
      headerName: 'Priority',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params) => {
        const { powerlistPriority } = params.row;

        // since priority 0 is Lowest we need to return null likk this
        if (!powerlistPriority && powerlistPriority !== 0) return '-';

        const priority = priorityRange.find(
          (p) => +p.value === +params.row.powerlistPriority
        );
        return priority?.name ? priority.name : '';
      },
    },
    {
      field: 'countUploaded',
      headerName: '# Uploaded',
      width: 140,
      renderCell: (params) =>
        nullIfHasMappingCrmId(params, () => params.row.countUploaded),
    },
    {
      field: 'countQueued',
      headerName: '# Queued',
      width: 140,
      renderCell: (params) => params.row.countQueued ?? '-',
    },
    {
      field: 'countDeferred',
      headerName: '# Deferred',
      width: 140,
      renderCell: (params) =>
        nullIfHasMappingCrmId(params, () => params.row.countDeferred),
    },
    {
      field: 'countRemoved',
      headerName: '# Removed',
      width: 140,
      renderCell: (params) =>
        nullIfHasMappingCrmId(params, () => params.row.countRemoved),
    },
    {
      field: 'subscribedCount',
      headerName: '# Agents',
      width: 140,
      renderCell: (params) =>
        nullIfHasMappingCrmId(params, () => params.row.subscribedCount),
    },
    {
      field: 'sessionCount',
      headerName: '# Agents In Session',
      width: 140,
      renderCell: (params) =>
        nullIfHasMappingCrmId(params, () => params.row.sessionCount),
    },
    {
      field: 'createdDate',
      headerName: 'Create Date',
      width: 100,
      renderCell: (params) =>
        !!params.value && typeof params.value === 'string'
          ? moment(params.value || undefined)?.format('MM/DD/YYYY')
          : '',
    },
    {
      field: '_actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      enableSorting: false,
      disableExport: true,
      disableSearch: true, // do not search field content
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params) => {
        return actionColumnFormatter(params);
      },
    },
  ] /* satisfies */ as IColDef[];

  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'name', sort: 'asc' },
  ]);
  const getLogo = () => {
    return toAbsoluteUrl('/media/logos/cadenceView.svg');
  };
  return (
    <>
      <DashboardTourExtraSpotlights />
      <Paper square={true} elevation={0}>
        <Switch>
          <Route
            path={
              profile
                ? `/my-profile/${agentId}/powerlists/:id/edit`
                : `/manage/agents/${agentId}/powerlists/:id/edit`
            }
          >
            {({ history, match }) => (
              <PowerlistModal
                agent={agentId}
                show={match != null}
                id={match?.params.id}
                handleClose={() => {
                  history.push(
                    profile
                      ? `/my-profile/${agentId}/powerlists`
                      : `/manage/agents/${agentId}/powerlists`
                  );
                  setRefetchToggleForPowerlists({});
                }}
                enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
                powerlistReportCache={teamPowerlistsCached}
              />
            )}
          </Route>
          <Route
            path={
              profile
                ? `/my-profile/${agentId}/powerlists/:id/delete`
                : `/manage/agents/${agentId}/powerlists/:id/delete`
            }
          >
            {({ history, match }) => (
              <DeletePowerlistModal
                handleDeletion={handleDeletion}
                show={match != null}
                deleteId={match?.params.id}
                handleClose={() => {
                  history.push(
                    profile
                      ? `/my-profile/${agentId}/powerlists`
                      : `/manage/agents/${agentId}/powerlists`
                  );
                  setRefetchToggleForPowerlists({});
                }}
              />
            )}
          </Route>
          <Route
            path={
              profile
                ? `/my-profile/${agentId}/powerlists/add`
                : `/manage/agents/${agentId}/powerlists/add`
            }
          >
            {({ history, match }) => (
              <PowerlistModal
                agent={agentId}
                id={null}
                show={match != null}
                handleClose={() => {
                  history.push(
                    profile
                      ? `/my-profile/${agentId}/powerlists`
                      : `/manage/agents/${agentId}/powerlists`
                  );
                  setRefetchToggleForPowerlists({});
                }}
                enqueueSnackbarWithStyles={enqueueSnackbarWithStyles as any}
                powerlistReportCache={teamPowerlistsCached}
              />
            )}
          </Route>
          <Route
            path={
              profile
                ? `/my-profile/${agentId}/powerlists/:id/spreadsheets`
                : `/manage/agents/${agentId}/powerlists/:id/spreadsheets`
            }
          >
            {({ history, match }) => (
              <SpreadsheetsModal
                showSpreadsheets={match != null}
                id={match?.params.id}
                handleHideSpreadsheets={() => {
                  history.push(
                    profile
                      ? `/my-profile/${agentId}/powerlists`
                      : `/manage/agents/${agentId}/powerlists`
                  );
                  setRefetchToggleForPowerlists({});
                }}
              />
            )}
          </Route>
        </Switch>
        {teamPowerlistsCached?.current?.length ? (
          <DataTable
            // passing JSON object as rows to DataTable causes react error
            // className={classes.dataTable}
            ToolbarComponent={ToolbarComponent}
            rows={teamPowerlistsCached.current ?? []}
            loading={teamPowerlistsLoadable.state !== 'hasValue'}
            columns={columns}
            sortModel={sortModel}
            setGridSortModel={setGridSortModel}
          />
        ) : (
          <Paper
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
            square={true}
            elevation={0}
          >
            <div
              style={{
                flexDirection: 'column',
                textAlign: 'center',
              }}
            >
              <h1 style={{ paddingTop: '35px' }}>
                <b>Well Hey There...</b>
              </h1>
              <h2>Let&apos;s add your first PowerList</h2>
              <img style={{ height: '500px' }} alt="logo" src={getLogo()} />
              <br />
              <Button
                className={_classes.button}
                name="ADD NEW POWERLIST"
                variant="contained"
                startIcon={<AddIcon />}
                color="primary"
                onClick={() => {
                  history.push(
                    profile
                      ? `/my-profile/${agentId}/powerlists/add`
                      : `/manage/agents/${agentId}/powerlists/add`
                  );
                }}
                disableElevation
                // disabled={disabled}
              >
                ADD NEW PERSONAL POWERLIST
              </Button>
              <br />
              <Button
                className={_classes.toolbarButton}
                variant="outlined"
                color="primary"
                endIcon={<HelpOutlineIcon />}
                target="_blank"
                href="https://support.kixie.com/hc/en-us/articles/4565589331483-How-to-Set-Up-PowerLists-"
                rel="noopener noreferrer"
              >
                LEARN MORE ABOUT POWERLIST
              </Button>
            </div>
          </Paper>
        )}
      </Paper>
    </>
  );
}
